import {
    collection,
    doc,
    getCountFromServer,
    getDoc,
    getDocs,
    getFirestore,
    limit,
    onSnapshot,
    orderBy,
    query,
    runTransaction,
    serverTimestamp,
    setDoc,
    startAfter,
} from './instrumentedFirestore';
import { getFirebaseApp } from './app';
import { createSecondaryAuthUser } from './secondaryAuthService';
import { teacherDocPath } from '@constants/firestorePaths';

function db() {
    return getFirestore(getFirebaseApp());
}

function stripUndefined(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    const next = {};
    for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'undefined') continue;
        next[k] = v;
    }
    return next;
}

function stripUndefinedDeep(value) {
    if (Array.isArray(value)) {
        const arr = value
            .map((v) => stripUndefinedDeep(v))
            .filter((v) => typeof v !== 'undefined');
        return arr;
    }
    if (value && typeof value === 'object') {
        // Preserve Firestore sentinel values and Timestamp-like objects.
        if (typeof value?.toDate === 'function') return value;
        const next = {};
        for (const [k, v] of Object.entries(value)) {
            const cleaned = stripUndefinedDeep(v);
            if (typeof cleaned === 'undefined') continue;
            if (cleaned && typeof cleaned === 'object' && !Array.isArray(cleaned) && Object.keys(cleaned).length === 0) continue;
            next[k] = cleaned;
        }
        if (Object.keys(next).length === 0) return undefined;
        return next;
    }
    if (typeof value === 'undefined') return undefined;
    return value;
}

function normalizeFirestoreValue(value) {
    if (!value) return value;
    if (typeof value?.toDate === 'function') {
        const d = value.toDate();
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    }
    return value;
}

function normalizeTeacherProfile(profile) {
    if (!profile || typeof profile !== 'object') return profile;
    return {
        ...profile,
        dateOfBirth: normalizeFirestoreValue(profile.dateOfBirth),
        currentPosition: profile.currentPosition
            ? {
                ...profile.currentPosition,
                joiningDate: normalizeFirestoreValue(profile.currentPosition.joiningDate),
            }
            : profile.currentPosition,
    };
}

function teachersCollectionRef(tenantId) {
    return collection(db(), 'Teachers', tenantId, 'teachers');
}

function teachersRootDocRef(tenantId) {
    return doc(db(), 'Teachers', tenantId);
}

/**
 * Recomputes `Teachers/{tenantId}` KPIs from all `teachers/*` docs (same idea as student root aggregates).
 * Call after create / update / soft-delete so counts stay aligned with Firestore.
 */
export async function refreshTenantTeachersSummary(tenantId) {
    if (!tenantId) throw new Error('Missing tenantId');
    const snap = await getDocs(teachersCollectionRef(tenantId));
    let totalTeachers = 0;
    let activeTeachers = 0;
    let inactiveTeachers = 0;
    let deletedTeachers = 0;

    for (const d of snap.docs) {
        const r = d.data();
        if (r.isDelete === true) {
            deletedTeachers += 1;
            continue;
        }
        totalTeachers += 1;
        const isActive = (r?.currentPosition?.status || 'active') !== 'inactive';
        if (isActive) activeTeachers += 1;
        else inactiveTeachers += 1;
    }

    await setDoc(
        teachersRootDocRef(tenantId),
        {
            tenantId,
            totalTeachers,
            activeTeachers,
            inactiveTeachers,
            deletedTeachers,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
}

export function subscribeTeacherKpis({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const rootRef = teachersRootDocRef(tenantId);
    return onSnapshot(
        rootRef,
        (snap) => {
            const data = snap.exists() ? snap.data() : {};
            onNext?.({
                totalTeachers: data.totalTeachers ?? 0,
                activeTeachers: data.activeTeachers ?? 0,
                inactiveTeachers: data.inactiveTeachers ?? 0,
                deletedTeachers: data.deletedTeachers ?? 0,
            });
        },
        (err) => onError?.(err)
    );
}

/**
 * Directory visibility: if `isDelete` is missing, treat as not deleted (`false`).
 * Hide only when `isDelete === true`.
 */
export function isDirectoryTeacher(row) {
    if (row == null) return false;
    return row.isDelete !== true;
}

function teachersListQuery(tenantId, { cursor = null, pageSize = null } = {}) {
    const baseRef = teachersCollectionRef(tenantId);
    const parts = [baseRef, orderBy('createdAt', 'desc')];
    if (cursor) parts.push(startAfter(cursor));
    if (pageSize != null) parts.push(limit(pageSize));
    return query(...parts);
}

export function subscribeTeachers({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const q = teachersListQuery(tenantId);
    return onSnapshot(
        q,
        (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(isDirectoryTeacher);
            onNext?.(rows);
        },
        (err) => onError?.(err)
    );
}

export async function fetchTeacherKpis({ tenantId }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const rootRef = teachersRootDocRef(tenantId);
    const snap = await getDoc(rootRef);
    const data = snap.exists() ? snap.data() : {};
    return {
        totalTeachers: data.totalTeachers ?? 0,
        activeTeachers: data.activeTeachers ?? 0,
        inactiveTeachers: data.inactiveTeachers ?? 0,
        deletedTeachers: data.deletedTeachers ?? 0,
    };
}

export async function fetchTeachersPage({ tenantId, pageSize = 10, cursor = null }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const baseRef = teachersCollectionRef(tenantId);
    const rootRef = teachersRootDocRef(tenantId);
    const rootSnap = await getDoc(rootRef);
    const rootData = rootSnap.exists() ? rootSnap.data() : {};
    const total =
        typeof rootData.totalTeachers === 'number'
            ? rootData.totalTeachers
            : (await getCountFromServer(baseRef)).data().count || 0;

    const q = teachersListQuery(tenantId, { cursor, pageSize });
    const snap = await getDocs(q);
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(isDirectoryTeacher);
    const nextCursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
    return { rows, nextCursor, total };
}

/**
 * Creates a teacher:
 * - Auth user (secondary auth) using email/password
 * - staff/{tenantId}/staff/{uid} so teacher can sign in to tenant portal
 * - Teachers/{tenantId}/teachers/{uid} full teacher profile (old-app keys)
 */
export async function createTeacher({ tenantId, email, password, profile }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!email || !password) throw new Error('Missing email or password');

    const created = await createSecondaryAuthUser({ email, password });
    const teacherUid = created.uid;

    const teacherRef = doc(db(), teacherDocPath(tenantId, teacherUid));

    const normalized = stripUndefinedDeep(stripUndefined({
        ...normalizeTeacherProfile(profile),
        teacherId: profile?.teacherId || null,
        email: created.email || email,
        authUid: teacherUid,
        tenantId,
        isDelete: false,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
    }));

    await runTransaction(db(), async (tx) => {
        tx.set(teacherRef, normalized || {}, { merge: true });
    });

    await refreshTenantTeachersSummary(tenantId);

    return { teacherUid, email: created.email || email };
}

export async function updateTeacherProfile({ tenantId, teacherId, patch }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!teacherId) throw new Error('Missing teacherId');
    const teacherRef = doc(db(), teacherDocPath(tenantId, teacherId));
    const normalized = stripUndefinedDeep(stripUndefined({
        ...normalizeTeacherProfile(patch),
        updatedAt: serverTimestamp(),
    }));
    await runTransaction(db(), async (tx) => {
        tx.set(teacherRef, normalized || {}, { merge: true });
    });
    await refreshTenantTeachersSummary(tenantId);
    return { teacherId };
}

/**
 * Soft-delete: sets `isDelete: true` on the teacher doc (does not remove auth or doc).
 * Updates `Teachers/{tenantId}` summary: decrements active or inactive, decrements totalTeachers, increments deletedTeachers.
 */
export async function softDeleteTeacher({ tenantId, teacherId }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!teacherId) throw new Error('Missing teacherId');
    const teacherRef = doc(db(), teacherDocPath(tenantId, teacherId));

    const preSnap = await getDoc(teacherRef);
    if (!preSnap.exists()) throw new Error('Teacher not found');
    if (preSnap.data()?.isDelete === true) return { teacherId };

    await runTransaction(db(), async (tx) => {
        tx.set(
            teacherRef,
            { isDelete: true, updatedAt: serverTimestamp() },
            { merge: true }
        );
    });

    await refreshTenantTeachersSummary(tenantId);
    return { teacherId };
}

